import { useState, useEffect } from "react";
import { GetResultsDto, Result } from "./api/Dtos";
import { DELETE, GET, PUT } from "./api/BackendApi";
import { useGlobalContext } from "./Layout/GlobalState";
import { Button, HStack, Input, Select, Text, VStack } from "@chakra-ui/react";

import { FastTable, isNumber } from "./Table/FastTable";
import { timeInMsToStringUTC, validTimeStr } from "./Services/ResultService";

const modifyFields = ["result"];
const modifyDisplayLabels = ["Tid"];

const fixedFields = [
  "firstname",
  "lastname",
  "birthYear",
  "startnumber",
  "class",
  "clubOrTeam",
];

const fixedDisplayLabels = [
  "Förnamn",
  "Efternamn",
  "Född",
  "Startnummer",
  "Klass",
  "Klubb/Lag",
];

type DisplayResult = Result & {
  result: string;
  resultInMs: number | null;
};

export function Results() {
  const globalContext = useGlobalContext();
  const [results, setResults] = useState<Array<DisplayResult>>([]);

  const [filterClass, setFilterClass] = useState<string | undefined>(undefined);
  const [filterStartnrOrName, setFilterStartnrOrName] = useState<
    string | undefined
  >(undefined);
  const [filterStartgroup, setFilterStartgroup] = useState<number | undefined>(
    undefined
  );

  const [filteredResults, setFilteredResults] = useState<Array<DisplayResult>>(
    []
  );

  // Get participants from backend
  useEffect(() => {
    if (globalContext.competition) {
      GET.allReportableResults(
        globalContext.competition.tag,
        globalContext.competition.startgroups
      ).then((response: GetResultsDto) => {
        const displayResults: Array<DisplayResult> = response.results
          .map((r) => {
            const resultInMs =
              r.startTime && r.finishTime
                ? r.finishTime - r.startTime
                : undefined;
            return {
              ...r,
              resultInMs: resultInMs,
              result: resultInMs ? timeInMsToStringUTC(resultInMs) : "-",
            } as DisplayResult & { resultInMs?: number };
          })
          .filter((r) => r.resultInMs != null)
          .sort((r1, r2) => (r1.resultInMs || 0) - (r2.resultInMs || 0))
          .map((r) => {
            return {
              ...r,
              resultInMs: r.resultInMs ?? -1,
            } as DisplayResult;
          });

        setResults(displayResults);
        setFilteredResults(displayResults);
      });
    }
  }, []);

  // Apply filtering
  useEffect(() => {
    let newResultsArray = [...results];
    if (filterClass) {
      newResultsArray = newResultsArray.filter(
        (r) => r.disciplineName === filterClass
      );
    }

    if (filterStartgroup) {
      newResultsArray = newResultsArray.filter(
        (r) => r.startgroup?.getTime() === filterStartgroup
      );
    }

    if (filterStartnrOrName) {
      if (isNumber(filterStartnrOrName)) {
        newResultsArray = newResultsArray.filter((r) => {
          return `${r.startnumber}`.startsWith(filterStartnrOrName);
        });
      } else {
        newResultsArray = newResultsArray.filter((r) =>
          `${r.firstname} ${r.lastname}`.includes(filterStartnrOrName)
        );
      }
    }

    setFilteredResults(
      newResultsArray.sort(
        (r1, r2) => (r1.resultInMs ?? 0) - (r2.resultInMs ?? 0)
      )
    );
  }, [results, filterClass, filterStartnrOrName, filterStartgroup]);

  return (
    <div>
      <HStack
        padding={"10px"}
        spacing={4}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        <VStack>
          <Text>Välj klass</Text>
          <Select
            minW={100}
            placeholder="Alla"
            onChange={(event) => {
              if (filterClass === event.currentTarget.value) {
                setFilterClass(undefined);
              } else {
                setFilterClass(event.currentTarget.value);
              }
            }}
          >
            {globalContext.competition?.disciplines.map((c) => {
              return (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack>
          <Text>Välj startgrupp</Text>
          <Select
            minW={100}
            placeholder="Alla"
            onChange={(event) => {
              const value = event.currentTarget.value;
              const numValue = value ? parseInt(value) : undefined;
              if (filterStartgroup === numValue) {
                setFilterStartgroup(undefined);
              } else {
                setFilterStartgroup(numValue);
              }
            }}
          >
            {globalContext.competition?.startgroups.map((sg) => {
              return (
                <option key={sg.id} value={sg.starttime_planned}>
                  {new Date(sg.starttime_planned).toLocaleString()}
                </option>
              );
            })}
          </Select>
        </VStack>
        <VStack>
          <Text>Sök på namn eller startnummer</Text>
          <Input
            minW={100}
            placeholder="Sök startnr eller namn"
            onChange={(event) => {
              if (filterStartnrOrName === event.currentTarget.value) {
                setFilterStartnrOrName(undefined);
              } else {
                setFilterStartnrOrName(event.currentTarget.value);
              }
            }}
          />
        </VStack>
      </HStack>

      <FastTable
        data={filteredResults}
        pageHeader={`Resultat: ${globalContext?.competition?.tag ?? ""} ${filterClass ?? ""}`}
        repeatHeaderRows={3}  // Freeze/repeat first 3 rows (page header + blank + column headers)
        downloadFileName="Resultat"
        headers={["Plats", "Nr", "Klass", "Löpare", "Klubb/Lag", "Tid"]}
        rowFormat={"1fr 1fr 1fr 4fr 4fr 2fr 1fr"}
        toRowData={(p) => {
          return [
            filterClass === undefined
              ? "-"
              : `${filteredResults.map((r) => r.result).indexOf(p.result) + 1}`,
            String(p.startnumber),
            p.disciplineName,
            `${p.firstname} ${p.lastname} (${p.birthYear})`,
            p.clubOrTeam,
            p.result,
          ];
        }}
        modifiableFields={modifyFields as Array<keyof Result>}
        modifiableFieldsDisplayName={modifyDisplayLabels}
        fixedFields={fixedFields as Array<keyof Result>}
        fixedFieldsDisplayName={fixedDisplayLabels}
        onChange={async (updatedResult) => {
          const okChange = validTimeStr(`${updatedResult.result}`);
          if (globalContext?.competition?.tag !== undefined) {
            if (okChange) {
              const success = await PUT.result(
                updatedResult,
                globalContext.competition.tag
              );
              if (success) {
                setResults((old) => {
                  return old.map((p) =>
                    p.id === updatedResult.id ? updatedResult : p
                  );
                });
              }
              return success;
            } else {
              return false;
            }
          } else {
            console.error("Ingen tävling är vald.");
            return false;
          }
        }}
      />
    </div>
  );
}
